import PortalIdParser from 'PortalIdParser';
import _capitalize from 'hs-lodash/capitalize';
import _differenceWith from 'hs-lodash/differenceWith';
import _isEqual from 'hs-lodash/isEqual';
import _isObject from 'hs-lodash/isObject';
import _uniq from 'hs-lodash/uniq';
import captureException from './captureException';

// compare two array of objects
export const isDataOutdated = (a, b, comparator = _isEqual) => {
  if (a.length !== b.length) return true;
  return _differenceWith(a, b, comparator).length !== 0;
};
const getUrlPath = url => url.split('?')[0].split(/\.[a-z]+/gi).pop();
export const requestUrlToI18nKey = url => {
  if (typeof url !== 'string') return '';
  return getUrlPath(url).split(/\/|-/gi).filter(v => v).map(v => _capitalize(v)) // capitalize
  .filter(v => {
    const regExp = new RegExp(/[0-9%_]+|contacts|deals|companies|tickets|meetings|contact|deal|company|ticket|meeting|zoom|apps|proxy|hubspot/gi);
    const versionRegExp = new RegExp(/^v[0-9]$/gi);
    if (versionRegExp.test(v)) return true;
    return !regExp.test(v);
  }) // remove ids
  .join('');
};
export const searchObjectFilterGroups = (list = []) => {
  if (!Array.isArray(list) || !list.length) {
    return [];
  }
  const values = list.map(item => item.id);
  return [{
    filters: [{
      values,
      propertyName: 'hs_object_id',
      operator: 'IN'
    }]
  }];
};
export const associationSectionPromise = ({
  callObjectId,
  toObjectType,
  fetchFunction,
  setSkipRefresh
}) => new Promise((resolve, reject) => {
  fetchFunction({
    callObjectId,
    toObjectType,
    setSkipRefresh
  }).then(({
    results
  }) => resolve({
    results,
    type: toObjectType
  })).catch(err => reject(err));
});
export const urlToAction = url => {
  if (typeof url !== 'string') return '';
  return getUrlPath(url).split('/').filter(v => {
    const regExp = new RegExp(/[0-9%]+/gi);
    const versionRegExp = new RegExp(/^v[0-9]$/gi);
    if (versionRegExp.test(v)) return true;
    if (v.includes('-')) return true;
    if (v.includes('_')) return true;
    return !regExp.test(v);
  }).join('/');
};
export const buildCreateCrmObjectAssociationsBody = crmObjectAssociations => {
  const entries = Object.entries(crmObjectAssociations);
  const result = [];
  for (const [associationTypeId, associationsIds] of entries) {
    for (const associationsId of associationsIds) {
      result.push({
        to: {
          id: associationsId
        },
        types: [{
          associationCategory: 'HUBSPOT_DEFINED',
          associationTypeId
        }]
      });
    }
  }
  return result;
};
export const getAssociationPropertyOptionTexts = ({
  crmObject,
  toObjectType
}) => {
  switch (toObjectType) {
    case 'COMPANY':
      {
        const name = crmObject.properties.name || '';
        const domain = crmObject.properties.domain || '';
        return Object.assign({
          text: name
        }, domain && {
          supportText: domain
        });
      }
    case 'DEAL':
      {
        const name = crmObject.properties.dealname || '';
        const amount = crmObject.properties.amount || '';
        return Object.assign({
          text: name
        }, amount && {
          supportText: amount
        });
      }
    case 'TICKET':
      {
        const name = crmObject.properties.subject || '';
        const status = crmObject.properties.status || '';
        return Object.assign({
          text: name
        }, status && {
          supportText: status
        });
      }
    case 'CONTACT':
      {
        const name = crmObject.properties.firstname || '';
        const lastname = crmObject.properties.lastname || '';
        const email = crmObject.properties.email || '';
        return Object.assign({
          text: `${name}${lastname ? ' ' : ''}${lastname}`
        }, email && {
          supportText: email
        });
      }
    default:
      return {
        text: ''
      };
  }
};
export const markResponseSentryEventAsHandled = response => {
  response.responseJSON = Object.assign({}, response.responseJSON, {
    sentryEventSent: true
  });
};
export function assignValueToAllObjectKeys(baseObject, extension) {
  if (_isObject(baseObject)) {
    return Object.keys(baseObject).reduce((acc, key) => {
      return Object.assign({}, acc, {
        [key]: extension
      });
    }, {});
  }
  return {};
}
export const buildSearchData = (offset, filters, query, properties) => {
  return {
    after: offset,
    limit: 10,
    sorts: ['firstname'],
    filterGroups: [{
      filters
    }],
    properties: _uniq(['firstname', 'lastname', 'email', 'photo', ...properties]),
    query: query || ''
  };
};
export const buildUrlFilters = (meetingID, currentCallObjectId) => {
  const filters = [];
  if (meetingID) {
    filters.push({
      operator: 'EQ',
      value: meetingID,
      property: 'hs_call_meeting_id'
    });
  }
  if (currentCallObjectId) {
    filters.push({
      operator: 'NEQ',
      value: currentCallObjectId,
      property: 'hs_object_id'
    });
  }
  try {
    return JSON.stringify(filters);
  } catch (err) {
    captureException(err);
  }
  return '';
};
export const addPortalIdParam = path => {
  const portalId = PortalIdParser.getPortalIdFromPath();
  if (!portalId) return path;
  if (path.includes('?')) return `${path}&portalId=${portalId}`;
  return `${path}?portalId=${portalId}`;
};

// this exported function is needed for unit tests to mock and spy on it
export const navigateWithReload = url => {
  window.location.href = url;
};